import { Link } from "react-router-dom";
import { createContext, useContext, useState, useMemo, useRef, useEffect } from "react";

/**
 * Shared UI for the admissions portal.
 *
 * Visual direction agreed with the school: the deep green stays, the page sits
 * on the warm neutral cream, form surfaces are white, body text is 16–18px,
 * borders are restrained and decorative cards are kept to a minimum. Controls
 * are at least 44px tall so they are comfortable on a phone.
 *
 * Bangla-first: every label takes `bn` and `en`, Bangla leads, and the toggle
 * switches which one is primary rather than printing both everywhere.
 */

/* ------------------------------------------------------------------ language */

const LangContext = createContext({ lang: "bn", setLang: () => {} });

export function LangProvider({ children, initial = "bn" }) {
  const [lang, setLang] = useState(initial);
  const value = useMemo(() => ({ lang, setLang }), [lang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

/** Pick the active language, falling back to whichever string exists. */
export function useT() {
  const { lang } = useLang();
  return (bn, en) => (lang === "bn" ? bn ?? en : en ?? bn);
}

/** Bangla text needs the Hind Siliguri stack; English should not use it. */
export function useFontClass() {
  const { lang } = useLang();
  return lang === "bn" ? "font-bn" : "font-sans";
}

export function LangToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex rounded-full border border-line bg-white p-0.5 ${className}`}
      role="group"
      aria-label="Language / ভাষা"
    >
      {[
        ["bn", "বাংলা", "font-bn"],
        ["en", "English", "font-sans"],
      ].map(([code, label, font]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`${font} min-h-[44px] px-4 rounded-full text-[14px] transition-colors ${
            lang === code ? "bg-green text-white font-semibold" : "text-muted hover:text-ink"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- layout */

/** Comfortable reading width. Forms stay narrow even on a wide monitor. */
export function PortalShell({ children, width = "form" }) {
  const t = useT();
  const font = useFontClass();
  const max = width === "wide" ? "max-w-[1100px]" : "max-w-[900px]";
  return (
    <div className="bg-cream min-h-[65vh]">
      <div className="relative overflow-hidden bg-green-dark text-white">
        <svg aria-hidden="true" className="absolute right-0 top-0 h-full w-1/2 opacity-[0.07]" viewBox="0 0 600 180"><g fill="none" stroke="currentColor">{Array.from({length:18},(_,i) => <circle key={i} cx={(i % 6) * 110 + 30} cy={Math.floor(i / 6) * 70 + 20} r="26" />)}</g></svg>
        <div className="relative max-w-container mx-auto px-4 sm:px-10 py-6 sm:py-8">
          <nav aria-label="Breadcrumb" className={`${font} flex flex-wrap items-center gap-2 text-sm text-white/70 mb-3`}>
            <Link className="hover:text-white underline-offset-4 hover:underline" to="/">{t('হোম', 'Home')}</Link><span aria-hidden="true">›</span>
            <Link className="hover:text-white underline-offset-4 hover:underline" to="/admissions-portal">{t('ভর্তি', 'Admissions')}</Link><span aria-hidden="true">›</span><span aria-current="page">{t('অনলাইন আবেদন', 'Online application')}</span>
          </nav>
          <p className={`${font} font-serif text-2xl sm:text-3xl font-bold`}>{t('অনলাইন ভর্তি', 'Online admissions')}</p>
          <p className={`${font} mt-2 text-sm sm:text-base text-white/75`}>{t('আবেদন, নথিপত্র ও ভর্তির অগ্রগতি — এক জায়গায়।', 'Your application, documents and admission updates in one place.')}</p>
        </div>
      </div>
      <div className={`${max} mx-auto px-4 sm:px-6 py-7 sm:py-10`}>{children}</div>
    </div>
  );
}

export function Surface({ children, className = "" }) {
  return <div className={`bg-white border border-line rounded-2xl ${className}`}>{children}</div>;
}

/* ------------------------------------------------------------------ controls */

const BASE_BTN =
  "inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-xl text-[16px] font-semibold " +
  "transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/60 focus-visible:ring-offset-2";

export function Button({ variant = "primary", className = "", ...props }) {
  const styles = {
    // One strong action per screen.
    primary: "bg-green text-white border-none hover:bg-green-mid",
    // Quieter companions: Back, Save & exit.
    secondary: "bg-white text-green border-[1.5px] border-green hover:bg-green-light",
    ghost: "bg-transparent text-muted border-none hover:text-ink underline",
  }[variant];
  return <button className={`${BASE_BTN} ${styles} ${className}`} {...props} />;
}

/**
 * One field. The error sits beside its own input — never only in a summary —
 * and `id` is what the error summary links to.
 */
export function Field({ id, label, hint, error, required, children, className = "" }) {
  const font = useFontClass();
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className={`${font} text-[15px] font-semibold text-ink`}>
        {label}
        {required && (
          <span className="text-red ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {hint && <span className={`${font} text-[13.5px] text-muted leading-snug`}>{hint}</span>}
      {error && (
        <span id={`${id}-error`} className={`${font} text-[13.5px] text-red font-semibold`} role="alert">
          {error}
        </span>
      )}
      {children}
    </div>
  );
}

const INPUT =
  // 16px keeps iOS from zooming the page when a field is focused.
  "w-full min-h-[48px] px-3.5 py-2.5 text-[16px] rounded-xl border-[1.5px] bg-white " +
  "outline-none transition-colors focus:border-green focus:ring-2 focus:ring-green/20";

export function TextInput({ id, error, className = "", ...props }) {
  return (
    <input
      id={id}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`${INPUT} ${error ? "border-red" : "border-line"} ${className}`}
      {...props}
    />
  );
}

export function TextArea({ id, error, className = "", ...props }) {
  return (
    <textarea
      id={id}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`${INPUT} resize-y ${error ? "border-red" : "border-line"} ${className}`}
      {...props}
    />
  );
}

export function Select({ id, error, className = "", children, ...props }) {
  return (
    <select
      id={id}
      aria-invalid={error ? "true" : undefined}
      className={`${INPUT} ${error ? "border-red" : "border-line"} ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

/* ------------------------------------------------------------- error summary */

/**
 * GOV.UK error-summary pattern: on a failed submit, focus moves here, every
 * problem is listed, and each entry jumps to the field that caused it. A parent
 * on a phone should never have to hunt a long form for what went wrong.
 */
export function ErrorSummary({ errors = [], className = "" }) {
  const t = useT();
  const font = useFontClass();
  const ref = useRef(null);

  useEffect(() => {
    if (errors.length) ref.current?.focus();
  }, [errors.length]);

  if (!errors.length) return null;

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      aria-labelledby="error-summary-title"
      className={`border-[2px] border-red rounded-2xl bg-white p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red/40 ${className}`}
    >
      <h2 id="error-summary-title" className={`${font} text-[18px] font-bold text-red mb-2`}>
        {t("কিছু তথ্য ঠিক করতে হবে", "There is a problem")}
      </h2>
      <ul className={`${font} text-[15px] list-disc pl-5 space-y-1.5`}>
        {errors.map((e) => (
          <li key={e.id}>
            <a
              href={`#${e.id}`}
              onClick={(ev) => {
                ev.preventDefault();
                const el = document.getElementById(e.id);
                el?.focus();
                el?.scrollIntoView({ block: "center", behavior: "smooth" });
              }}
              className="text-red underline font-semibold"
            >
              {e.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------- navigation */

/** "ধাপ ২ / ৫" plus the section title, and a way back to the whole list. */
export function SectionHeader({ step, total, title, onViewAll }) {
  const t = useT();
  const font = useFontClass();
  const pct = Math.round((step / total) * 100);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <span className={`${font} text-[14px] font-semibold text-green`}>
          {t(`ধাপ ${toBn(step)} / ${toBn(total)}`, `Section ${step} of ${total}`)}
        </span>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className={`${font} text-[14px] text-green underline bg-transparent border-none cursor-pointer min-h-[44px] px-1`}
          >
            {t("সব ধাপ দেখুন", "View all sections")}
          </button>
        )}
      </div>
      <div className="h-1.5 rounded-full bg-line overflow-hidden mb-4" aria-hidden="true">
        <div className="h-full bg-green rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <h1
        className={`${font} font-serif text-[26px] sm:text-[30px] font-bold text-green-dark leading-tight`}
      >
        {title}
      </h1>
    </div>
  );
}

/** Saved / unsaved state. Must reflect reality — never claim a save that failed. */
export function SaveState({ state = "saved", at }) {
  const t = useT();
  const font = useFontClass();
  const map = {
    saved: { text: t("সংরক্ষিত", "Saved"), cls: "text-green", dot: "bg-green" },
    unsaved: { text: t("সংরক্ষিত হয়নি", "Unsaved changes"), cls: "text-gold", dot: "bg-gold" },
    saving: { text: t("সংরক্ষণ হচ্ছে…", "Saving…"), cls: "text-muted", dot: "bg-muted" },
    failed: { text: t("সংরক্ষণ ব্যর্থ", "Save failed"), cls: "text-red", dot: "bg-red" },
  }[state];
  return (
    <span className={`${font} inline-flex items-center gap-2 text-[13.5px] ${map.cls}`}>
      <span className={`w-2 h-2 rounded-full ${map.dot}`} aria-hidden="true" />
      {map.text}
      {state === "saved" && at && <span className="text-muted">&nbsp;· {at}</span>}
    </span>
  );
}

/** Not started / In progress / Complete, for the section checklist. */
export function StatusChip({ status }) {
  const t = useT();
  const font = useFontClass();
  const map = {
    complete: { text: t("সম্পন্ন", "Complete"), cls: "bg-green-light text-green-dark" },
    progress: { text: t("চলছে", "In progress"), cls: "bg-gold-light text-gold" },
    todo: { text: t("শুরু হয়নি", "Not started"), cls: "bg-[#f2f4f3] text-muted" },
  }[status];
  return (
    <span className={`${font} text-[12.5px] font-semibold rounded-full px-2.5 py-1 ${map.cls}`}>
      {map.text}
    </span>
  );
}

/** ASCII digits to Bangla, for counts shown in Bangla copy. */
export const toBn = (n) => String(n).replace(/\d/g, (d) => "০১২৩৪৫৬৭৮৯"[d]);
